import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCopy, FiCheck, FiClock, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const PixPayment = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [order, setOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);

  const loadOrder = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${orderId}`);
      setOrder(response.data);
      setPaymentStatus(response.data.payment_status);
      
      if (response.data.payment_status === 'paid') {
        // Redirect to tracking if already paid
        setTimeout(() => navigate(`/cliente/pedido/${orderId}`), 2000);
      }
    } catch (err) {
      setError('Erro ao carregar pedido');
    }
  }, [orderId, navigate]);

  const generatePix = async () => {
    setGenerating(true);
    setError('');
    
    try {
      const response = await api.post(`/payments/pix/${orderId}`);
      setPixData(response.data);
      
      // Calculate time left (30 minutes default)
      if (response.data.expiration_date) {
        const expiration = new Date(response.data.expiration_date);
        const now = new Date();
        const diff = Math.max(0, Math.floor((expiration - now) / 1000));
        setTimeLeft(diff);
      } else {
        setTimeLeft(30 * 60); // 30 minutes default
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao gerar PIX');
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await api.get(`/payments/status/${orderId}`);
      setPaymentStatus(response.data.payment_status);
      
      if (response.data.payment_status === 'paid') {
        setTimeout(() => navigate(`/cliente/pedido/${orderId}`), 2000);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  useEffect(() => {
    if (order && order.payment_method === 'pix' && paymentStatus !== 'paid') {
      generatePix();
    } else {
      setLoading(false);
    }
  }, [order]);

  // Poll payment status every 5 seconds
  useEffect(() => {
    if (pixData && paymentStatus === 'pending') {
      const interval = setInterval(checkPaymentStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [pixData, paymentStatus]);

  // Countdown timer
  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => Math.max(0, prev - 1));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft]);

  const copyPixCode = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">Gerando código PIX...</p>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'paid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheck className="text-4xl text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Pagamento Confirmado!</h2>
          <p className="text-gray-600">Redirecionando para acompanhar seu pedido...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-green-600 to-green-800 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(`/cliente/pedido/${orderId}`)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <div>
            <h1 className="font-semibold">Pagamento PIX</h1>
            <p className="text-sm text-green-100">Pedido #{orderId?.slice(0, 8)}</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="p-4 bg-red-100 border border-red-300 text-red-700 rounded-xl flex items-center gap-3">
            <FiAlertCircle className="text-xl flex-shrink-0" />
            <div>
              <p className="font-medium">{error}</p>
              <button 
                onClick={generatePix}
                className="text-sm underline hover:no-underline mt-1"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        )}

        {pixData && (
          <>
            {/* Timer */}
            {timeLeft !== null && (
              <div className={`rounded-xl p-4 flex items-center justify-between ${
                timeLeft < 300 ? 'bg-red-50 border-2 border-red-300' : 'bg-yellow-50 border-2 border-yellow-300'
              }`}>
                <div className="flex items-center gap-2">
                  <FiClock className={timeLeft < 300 ? 'text-red-600' : 'text-yellow-600'} />
                  <span className={`font-medium ${timeLeft < 300 ? 'text-red-700' : 'text-yellow-700'}`}>
                    Expira em:
                  </span>
                </div>
                <span className={`text-2xl font-bold font-mono ${timeLeft < 300 ? 'text-red-600' : 'text-yellow-600'}`}>
                  {formatTime(timeLeft)}
                </span>
              </div>
            )}

            {/* Total Value */}
            <div className="bg-white rounded-xl p-5 shadow-sm text-center">
              <p className="text-gray-500 mb-1">Valor a Pagar</p>
              <p className="text-4xl font-bold text-green-600">{formatCurrency(pixData.total_value)}</p>
            </div>

            {/* QR Code */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-4 text-center">Escaneie o QR Code</h3>
              <div className="flex justify-center mb-4">
                {pixData.qr_code_base64 ? (
                  <img 
                    src={`data:image/png;base64,${pixData.qr_code_base64}`}
                    alt="QR Code PIX"
                    className="w-64 h-64 border-4 border-gray-100 rounded-xl"
                  />
                ) : (
                  <div className="w-64 h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                    <p className="text-gray-500 text-center px-4">
                      QR Code não disponível.<br/>Use o código abaixo.
                    </p>
                  </div>
                )}
              </div>
              <p className="text-sm text-gray-500 text-center">
                Abra o app do seu banco e escaneie o código acima
              </p>
            </div>

            {/* PIX Copy Paste */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <h3 className="font-semibold text-gray-800 mb-3">Ou copie o código PIX</h3>
              <div className="relative">
                <div className="bg-gray-50 rounded-xl p-4 pr-16 font-mono text-xs break-all text-gray-600 max-h-24 overflow-y-auto">
                  {pixData.qr_code}
                </div>
                <button
                  onClick={copyPixCode}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-3 rounded-lg transition-colors ${
                    copied 
                      ? 'bg-green-100 text-green-600' 
                      : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                  }`}
                >
                  {copied ? <FiCheck /> : <FiCopy />}
                </button>
              </div>
              {copied && (
                <p className="text-green-600 text-sm mt-2 text-center">✓ Código copiado!</p>
              )}
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h3 className="font-semibold text-blue-800 mb-3">Como pagar:</h3>
              <ol className="space-y-2 text-sm text-blue-700">
                <li className="flex gap-2">
                  <span className="font-bold">1.</span>
                  Abra o app do seu banco ou carteira digital
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">2.</span>
                  Escolha pagar com PIX e escaneie o QR Code ou cole o código
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">3.</span>
                  Confirme o pagamento
                </li>
                <li className="flex gap-2">
                  <span className="font-bold">4.</span>
                  Pronto! O pagamento é confirmado automaticamente em segundos
                </li>
              </ol>
            </div>

            {/* Status Check */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${
                    paymentStatus === 'pending' ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'
                  }`} />
                  <span className="text-gray-600">
                    {paymentStatus === 'pending' ? 'Aguardando pagamento...' : 'Pagamento confirmado!'}
                  </span>
                </div>
                <button
                  onClick={checkPaymentStatus}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Verificar pagamento"
                >
                  <FiRefreshCw />
                </button>
              </div>
            </div>
          </>
        )}

        {generating && (
          <div className="bg-white rounded-xl p-8 shadow-sm text-center">
            <LoadingSpinner />
            <p className="mt-4 text-gray-600">Gerando código PIX...</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default PixPayment;
